import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, TrendingUp, Wallet, FolderKanban, Send, FileDown, FileSpreadsheet, HandCoins, Hourglass, Handshake, Scissors } from "lucide-react";
import { api, API, apiError } from "@/lib/api";

const money = (v) => `$${Number(v || 0).toLocaleString("en-NZ", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const STATUS_COLORS = {
  "Open": "#58635C", "In Progress": "#8A6D2F", "Recovered": "#3F6B4E", "Closed": "#0A0D0B",
};
const BUCKET_COLORS = { "0-30": "#3F6B4E", "31-60": "#8A6D2F", "61-90": "#B0684A", "90+": "#8E3B2F" };

export default function ReportsPanel() {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/reports/summary").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail) || "Could not load reports.")); }, []);

  const sendDigest = async () => {
    setSending(true);
    try {
      const { data: r } = await api.post("/admin/send-digest");
      toast.success(r.count ? `Digest for ${r.count} matter(s) sent to ${r.sent_to}.` : (r.message || "No matters currently need attention."));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setSending(false); }
  };

  if (!data) return <Loader2 className="animate-spin text-[#B89874] mt-8" />;
  const totalActive = (data.by_status["Open"] || 0) + (data.by_status["In Progress"] || 0);
  const max = Math.max(1, ...Object.values(data.by_status));
  const aged = data.aged || { buckets: [], total_outstanding: 0, total_count: 0 };
  const agedMax = Math.max(1, ...aged.buckets.map((b) => b.amount));
  const rows = data.rows || [];

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[#0A0D0B]">Reports</h1>
          <p className="mt-2 text-[#58635C]">A snapshot of recovery across every client file.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <a href={`${API}/reports/export?format=pdf`} target="_blank" rel="noreferrer" data-testid="export-pdf-btn"
            className="inline-flex items-center gap-2 bg-[#0A0D0B] text-white px-5 py-3 text-sm font-semibold hover:bg-[#233027] transition-colors">
            <FileDown size={15} /> Export PDF
          </a>
          <a href={`${API}/reports/export?format=csv`} target="_blank" rel="noreferrer" data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 border border-[#0A0D0B] text-[#0A0D0B] px-5 py-3 text-sm font-semibold hover:bg-[#0A0D0B] hover:text-white transition-colors">
            <FileSpreadsheet size={15} /> Export CSV
          </a>
          <button onClick={sendDigest} disabled={sending} data-testid="send-digest-btn"
            className="inline-flex items-center gap-2 border border-[#E5E1D8] text-[#58635C] px-5 py-3 text-sm font-semibold hover:border-[#0A0D0B] hover:text-[#0A0D0B] transition-colors disabled:opacity-60">
            {sending ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Follow-up list
          </button>
        </div>
      </div>

      <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="reports-cards">
        <Card icon={FolderKanban} label="Total matters" value={data.total_matters} sub={`${data.clients} client${data.clients === 1 ? "" : "s"}`} />
        <Card icon={Wallet} label="Outstanding" value={money(data.outstanding_amount)} sub={`${totalActive} active matter${totalActive === 1 ? "" : "s"}`} accent="#8A6D2F" />
        <Card icon={HandCoins} label="Payments received" value={money(data.payments_received)} sub="part-payments logged" accent="#3F6B4E" />
        <Card icon={TrendingUp} label="Recovered" value={money(data.recovered_amount)} sub={`${data.by_status["Recovered"] || 0} recovered`} accent="#3F6B4E" />
      </div>

      {data.settled_count > 0 && (
        <div className="mt-4 grid sm:grid-cols-3 gap-4" data-testid="settlement-report">
          <Card icon={Handshake} label="Settled (full & final)" value={money(data.settled_total)} sub={`${data.settled_count} settlement${data.settled_count === 1 ? "" : "s"} agreed`} accent="#8A6D2F" />
          <Card icon={Scissors} label="Total written off" value={money(data.written_off_total)} sub="waived to secure settlement" accent="#8A6D2F" />
          <Card icon={TrendingUp} label="Value of settling" value={data.settled_total + data.written_off_total > 0 ? `${Math.round((data.settled_total / (data.settled_total + data.written_off_total)) * 100)}%` : "—"} sub="recovered vs original on settled matters" accent="#3F6B4E" />
        </div>
      )}

      <div className="mt-8 grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-[#E5E1D8] p-6">
          <div className="text-[11px] uppercase tracking-[0.16em] text-[#58635C] mb-4">Matters by status</div>
          <div className="space-y-3">
            {Object.entries(data.by_status).map(([status, count]) => (
              <div key={status} className="flex items-center gap-4" data-testid={`report-status-${status.replace(/\s+/g, "-").toLowerCase()}`}>
                <span className="w-28 text-sm text-[#233027] shrink-0">{status}</span>
                <div className="flex-1 h-6 bg-[#F5F3EC] overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${(count / max) * 100}%`, background: STATUS_COLORS[status] }} />
                </div>
                <span className="w-10 text-right text-sm font-mono-gg text-[#0A0D0B] shrink-0">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-[#E5E1D8] p-6" data-testid="aged-debt-panel">
          <div className="flex items-center justify-between mb-4">
            <div className="text-[11px] uppercase tracking-[0.16em] text-[#58635C]">Aged debt · outstanding</div>
            <span className="inline-flex items-center gap-1.5 text-xs text-[#8A6D2F]"><Hourglass size={13} /> aged from opened date</span>
          </div>
          <div className="space-y-3">
            {aged.buckets.map((b) => (
              <div key={b.key} className="flex items-center gap-4" data-testid={`aged-bucket-${b.key}`}>
                <span className="w-24 text-sm text-[#233027] shrink-0">{b.label}</span>
                <div className="flex-1 h-6 bg-[#F5F3EC] overflow-hidden">
                  <div className="h-full transition-all" style={{ width: `${(b.amount / agedMax) * 100}%`, background: BUCKET_COLORS[b.key] }} />
                </div>
                <span className="w-14 text-right text-xs text-[#58635C] shrink-0">{b.count}</span>
                <span className="w-24 text-right text-sm font-mono-gg text-[#0A0D0B] shrink-0">{money(b.amount)}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-[#F0EDE4] flex justify-between text-sm">
            <span className="text-[#58635C]">Total outstanding · {aged.total_count} matter{aged.total_count === 1 ? "" : "s"}</span>
            <span className="font-mono-gg text-[#0A0D0B]">{money(aged.total_outstanding)}</span>
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-[#E5E1D8] p-6" data-testid="outstanding-matters-table">
        <div className="text-[11px] uppercase tracking-[0.16em] text-[#58635C] mb-4">Outstanding matters ({rows.length})</div>
        {rows.length === 0 ? (
          <p className="text-sm text-[#58635C]">No outstanding matters — everything is recovered or closed.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-[0.12em] text-[#58635C] border-b border-[#E5E1D8]">
                  <th className="py-2 pr-3">Client</th><th className="py-2 pr-3">Party</th>
                  <th className="py-2 pr-3">Status</th><th className="py-2 pr-3 text-right">Age</th>
                  <th className="py-2 pr-3 text-right">Original</th><th className="py-2 pr-3 text-right">Paid</th>
                  <th className="py-2 text-right">Outstanding</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.matter_id} data-testid={`report-row-${r.matter_id}`} className="border-b border-[#F0EDE4]">
                    <td className="py-2 pr-3 text-[#233027]">{r.client}</td>
                    <td className="py-2 pr-3 text-[#0A0D0B]">{r.party}</td>
                    <td className="py-2 pr-3 text-[#58635C]">{r.status}</td>
                    <td className="py-2 pr-3 text-right font-mono-gg" style={{ color: BUCKET_COLORS[r.bucket] }}>{r.age_days}d</td>
                    <td className="py-2 pr-3 text-right font-mono-gg text-[#58635C]">{money(r.original)}</td>
                    <td className="py-2 pr-3 text-right font-mono-gg text-[#3F6B4E]">{money(r.paid)}</td>
                    <td className="py-2 text-right font-mono-gg text-[#0A0D0B]">{money(r.outstanding)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Card({ icon: Icon, label, value, sub, accent = "#0A0D0B" }) {
  return (
    <div className="bg-white border border-[#E5E1D8] p-5">
      <Icon size={18} style={{ color: accent }} />
      <div className="text-2xl font-display text-[#0A0D0B] mt-3">{value}</div>
      <div className="text-[11px] uppercase tracking-[0.16em] text-[#58635C] mt-1">{label}</div>
      <div className="text-xs text-[#B89874] mt-1">{sub}</div>
    </div>
  );
}
